'use client';

import type Hls from 'hls.js/dist/hls.light.mjs';
import type { CSSProperties } from 'react';
import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { z } from 'zod';
import { logDebug, logError } from '@/lib/logging-utils';
const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';
const HLS_SOURCE_REGEX = /\.m3u8($|\?)/i;
const isHlsSource = (source: string): boolean => HLS_SOURCE_REGEX.test(source);
const HlsErrorSchema = z.object({
  fatal: z.boolean().optional()
});
interface TweetVideoPlayerProperties {
  className?: string;
  controls?: boolean;
  height?: number | string;
  loop?: boolean;
  muted?: boolean;
  onPlay?: () => void;
  onReady?: () => void;
  onStart?: () => void;
  playing?: boolean;
  playsInline?: boolean;
  poster?: string;
  src: string;
  style?: CSSProperties;
  volume?: number;
  width?: number | string;
}
export function TweetVideoPlayer(properties: TweetVideoPlayerProperties) {
  const {
    src,
    playing = false,
    controls = false,
    loop = false,
    muted = false,
    playsInline = true,
    poster,
    width,
    height,
    volume,
    style,
    className,
    onReady,
    onStart,
    onPlay
  } = properties;
  const videoReference = useRef<HTMLVideoElement | null>(null);
  const hlsReference = useRef<Hls | null>(null);
  const hasStartedReference = useRef(false);
  const emitReady = useEffectEvent(() => {
    onReady?.();
  });
  const emitStart = useEffectEvent(() => {
    onStart?.();
  });
  const emitPlay = useEffectEvent(() => {
    onPlay?.();
  });
  useEffect(() => {
    hasStartedReference.current = false;
    const video = videoReference.current;
    if (!(video && src) || globalThis.window === undefined) {
      return;
    }
    const cancellation = {
      current: false
    };
    const cleanupHls = () => {
      const instance = hlsReference.current;
      if (instance) {
        instance.destroy();
        hlsReference.current = null;
      }
    };
    const loadNativeSource = () => {
      if (cancellation.current) {
        return;
      }
      cleanupHls();
      try {
        if (video.src !== src) {
          video.src = src;
        }
        video.load();
      } catch (error) {
        logError('TweetVideoPlayer failed to attach native source', {
          error,
          src
        }, 'ui');
      }
    };
    const setupHls = async () => {
      try {
        const {
          default: HlsModule
        } = await import('hls.js/dist/hls.light.mjs');
        if (cancellation.current) {
          return;
        }
        if (!HlsModule?.isSupported?.()) {
          loadNativeSource();
          return;
        }
        const hls = new HlsModule({
          enableWorker: true,
          lowLatencyMode: true
        });
        hlsReference.current = hls;
        const events = HlsModule.Events;
        hls.on(events.ERROR, (...arguments_: unknown[]) => {
          const [, rawData] = arguments_;
          const parsedError = HlsErrorSchema.safeParse(rawData);
          const errorPayload = parsedError.success ? parsedError.data : null;
          const isFatalError = errorPayload?.fatal === true;
          if (!isFatalError) {
            return;
          }
          logError('TweetVideoPlayer fatal HLS error', {
            data: errorPayload,
            src
          }, 'ui');
          hls.destroy();
          hlsReference.current = null;
          loadNativeSource();
        });
        hls.attachMedia(video);
        hls.on(events.MEDIA_ATTACHED, () => {
          if (cancellation.current) {
            return;
          }
          hls.loadSource(src);
        });
      } catch (error_0) {
        if (!cancellation.current) {
          logError('TweetVideoPlayer failed to lazy-load hls.js', {
            error: error_0,
            src
          }, 'ui');
          loadNativeSource();
        }
      }
    };
    if (isHlsSource(src)) {
      const canPlayNative = video.canPlayType(HLS_MIME_TYPE);
      if (canPlayNative) {
        loadNativeSource();
      } else {
        video.removeAttribute('src');
        video.load();
        setupHls().catch((error_1: unknown) => {
          logError('TweetVideoPlayer failed to initialize HLS', {
            error: error_1,
            src
          }, 'ui');
        });
      }
    } else {
      loadNativeSource();
    }
    return () => {
      cancellation.current = true;
      cleanupHls();
    };
  }, [src]);
  useEffect(() => {
    const video_0 = videoReference.current;
    if (!video_0) {
      return;
    }
    const handleLoadedMetadata = () => {
      emitReady();
    };
    const handlePlay = () => {
      if (!hasStartedReference.current) {
        hasStartedReference.current = true;
        emitStart();
      }
      emitPlay();
    };
    video_0.addEventListener('loadedmetadata', handleLoadedMetadata);
    video_0.addEventListener('play', handlePlay);
    return () => {
      video_0.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video_0.removeEventListener('play', handlePlay);
    };
    // Effect events are stable; listeners only depend on DOM refs.
  }, []);
  useEffect(() => {
    const video_1 = videoReference.current;
    if (!video_1) {
      return;
    }
    if (typeof volume === 'number') {
      video_1.volume = Math.min(1, Math.max(0, volume));
    }
  }, [volume]);
  useEffect(() => {
    const video_2 = videoReference.current;
    if (!video_2) {
      return;
    }
    const attemptPlay = () => {
      const playResult = video_2.play();
      if (!(playResult instanceof Promise)) {
        return;
      }
      playResult.catch((error_2: unknown) => {
        if (isAutoplayBlocked(error_2)) {
          logDebug('TweetVideoPlayer autoplay blocked by browser policy', {
            src
          }, 'ui');
          return;
        }
        logError('TweetVideoPlayer play() failed', {
          error: error_2,
          src
        }, 'ui');
      });
    };
    if (!playing) {
      video_2.pause();
      return;
    }
    if (video_2.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
      return;
    }
    const handleCanPlay = () => {
      video_2.removeEventListener('canplay', handleCanPlay);
      attemptPlay();
    };
    video_2.addEventListener('canplay', handleCanPlay);
    return () => {
      video_2.removeEventListener('canplay', handleCanPlay);
    };
  }, [playing, src]);
  const resolvedStyle = useMemo(() => {
    const base: CSSProperties = {
      ...style
    };
    if (width !== undefined) {
      base.width = typeof width === 'number' ? `${width}px` : width;
    }
    if (height !== undefined) {
      base.height = typeof height === 'number' ? `${height}px` : height;
    }
    return base;
  }, [height, style, width]);
  return <video ref={videoReference} className={className} controls={controls} loop={loop} muted={muted} playsInline={playsInline} poster={poster} preload="metadata" style={resolvedStyle} tabIndex={-1} />;
}
function isAutoplayBlocked(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const name = 'name' in error ? (error as {
    name?: unknown;
  }).name : undefined;
  if (typeof name !== 'string') {
    return false;
  }
  return name === 'NotAllowedError' || name === 'AbortError';
}